import { RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  ExternalLink,
  InfraCodeBlock,
  InfraField,
  InfraSection,
  Text,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';
import { labContextNameFromCluster } from './labKubectl';

function shellSingleQuote(value: string): string {
  return `'${value.replace(/'/g, `'\"'\"'`)}'`;
}

export function buildOcSetupScript(
  kubeconfigBase64: string,
  labContextName: string,
): string {
  const b64 = kubeconfigBase64.replace(/\s+/g, '');
  const cfgFile = `\${HOME}/.kube/${labContextName}-kubeconfig`;
  return [
    'set -eu',
    `CFG_FILE=${cfgFile}`,
    'mkdir -p "${HOME}/.kube"',
    `echo ${shellSingleQuote(b64)} | base64 -d > "$CFG_FILE"`,
    'chmod 600 "$CFG_FILE"',
    'export KUBECONFIG="$CFG_FILE"',
    'command -v oc >/dev/null 2>&1 || { echo "Install oc CLI: https://mirror.openshift.com/pub/openshift-v4/clients/ocp/"; exit 1; }',
    'oc whoami',
    'oc get nodes',
    'echo ""',
    'echo "Kubeconfig: $CFG_FILE"',
    'echo "Reuse: export KUBECONFIG=$CFG_FILE"',
  ].join('\n');
}

export const OC_SETUP_SCRIPT_PREVIEW = `set -eu
CFG_FILE=$HOME/.kube/ps-lab-<cluster>-kubeconfig
echo '<kubeconfig-base64>' | base64 -d > "$CFG_FILE"
chmod 600 "$CFG_FILE"
export KUBECONFIG="$CFG_FILE"
oc whoami
oc get nodes`;

type LabOcSectionProps = {
  clusterName?: string;
  environmentName?: string;
  kubeconfigBase64?: string;
  kubeadminPasswordBase64?: string;
  consoleUrl?: string;
  redisNamespace?: string;
};

export function LabOcSection({
  clusterName,
  environmentName,
  kubeconfigBase64,
  kubeadminPasswordBase64,
  consoleUrl,
  redisNamespace,
}: LabOcSectionProps) {
  if (!kubeconfigBase64 && !kubeadminPasswordBase64) {
    return null;
  }

  const labContextName = labContextNameFromCluster(clusterName, environmentName);

  const copySetupScript = () => {
    if (!kubeconfigBase64) {
      return;
    }
    navigator.clipboard.writeText(
      buildOcSetupScript(kubeconfigBase64, labContextName),
    );
  };

  const kubeadminPassword = (() => {
    if (!kubeadminPasswordBase64) {
      return '';
    }
    try {
      return atob(kubeadminPasswordBase64.replace(/\s+/g, ''));
    } catch {
      return '';
    }
  })();

  return (
    <>
      {kubeadminPassword && (
        <InfraSection title="Web console login">
          <InfraField label="User" value="kubeadmin" />
          <InfraField
            label="Password"
            value={
              <span className={styles.copyRow}>
                <span className={styles.value}>••••••••</span>
                <CopyButton
                  text={kubeadminPassword}
                  label="Copy kubeadmin password"
                />
              </span>
            }
            mono={false}
          />
          {consoleUrl && (
            <InfraField
              label="Console"
              value={<ExternalLink href={consoleUrl}>Open console</ExternalLink>}
              mono={false}
            />
          )}
          <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
            kubeadmin is temporary bootstrap access. Lab / TTL environment only.
          </Text>
        </InfraSection>
      )}

      {kubeconfigBase64 && (
        <InfraSection title="Lab oc CLI">
          <Text variant="body-medium">
            Click <strong>Copy setup script</strong>, paste in Terminal, press Enter. Writes a
            dedicated kubeconfig to <code>~/.kube/{labContextName}-kubeconfig</code> and runs{' '}
            <code>oc whoami</code> — same one-shot flow as GKE/EKS lab kubectl.
          </Text>
          <Button
            variant="primary"
            size="small"
            onPress={copySetupScript}
            style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
          >
            <RiFileCopyLine size={16} />
            Copy setup script
          </Button>
          <InfraCodeBlock variant="light">{OC_SETUP_SCRIPT_PREVIEW}</InfraCodeBlock>
          <Text variant="body-medium" style={{ marginTop: '1rem' }}>
            Day to day: <code>export KUBECONFIG=~/.kube/{labContextName}-kubeconfig</code>
            <br />
            <code>oc get ns</code>
            {redisNamespace && (
              <>
                <br />
                <code>oc project {redisNamespace}</code>
              </>
            )}
          </Text>
        </InfraSection>
      )}
    </>
  );
}
